import React, { useState, useEffect } from 'react';
import { apiRequest } from '../services/api';
import { CheckCircle, ArrowUp, Compass, Target, BookOpen } from '@phosphor-icons/react';

const CATEGORIES = [
  {
    key: 'proficient',
    label: 'Confident',
    Icon: CheckCircle,
    colorBg: 'bg-green-100',
    colorText: 'text-green-800',
    colorBar: 'bg-green-500',
    colorLabel: 'text-green-700',
    colorBorder: 'border-l-green-400',
  },
  {
    key: 'developing',
    label: 'Getting There',
    Icon: ArrowUp,
    colorBg: 'bg-yellow-100',
    colorText: 'text-yellow-800',
    colorBar: 'bg-yellow-500',
    colorLabel: 'text-yellow-700',
    colorBorder: 'border-l-yellow-400',
  },
  {
    key: 'exploring',
    label: 'Just Started',
    Icon: Compass,
    colorBg: 'bg-blue-100',
    colorText: 'text-blue-800',
    colorBar: 'bg-blue-500',
    colorLabel: 'text-blue-700',
    colorBorder: 'border-l-blue-400',
  },
  {
    key: 'struggling',
    label: 'Needs Practice',
    Icon: Target,
    colorBg: 'bg-red-100',
    colorText: 'text-red-800',
    colorBar: 'bg-red-500',
    colorLabel: 'text-red-700',
    colorBorder: 'border-l-red-400',
  },
];

const ProgressTracker = ({ classSectionId }) => {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        setLoading(true);
        let url = '/api/student/progress';
        if (classSectionId) url += `?class_section_id=${classSectionId}`;
        const data = await apiRequest(url, 'GET');
        setProgress(data);
      } catch {
        setProgress({ struggling: [], exploring: [], developing: [], proficient: [] });
      } finally {
        setLoading(false);
      }
    };
    fetchProgress();
  }, [classSectionId]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">My Progress</h3>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
              <div className="flex gap-2">
                <div className="h-8 bg-gray-200 rounded-full w-20"></div>
                <div className="h-8 bg-gray-200 rounded-full w-24"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const hasData = progress && CATEGORIES.some(
    cat => (progress[cat.key] || []).length > 0
  );

  if (!hasData) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">My Progress</h3>
        <div className="text-center py-6 text-gray-500">
          <div className="w-14 h-14 rounded-full bg-indigo-50 flex items-center justify-center mx-auto mb-3 animate-float">
            <BookOpen size={28} className="text-indigo-500" />
          </div>
          <p className="text-lg mb-1">Start chatting to see your progress!</p>
          <p className="text-sm">Your topic mastery will appear here as you learn.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-xl font-semibold text-gray-900 mb-4">My Progress</h3>

      {CATEGORIES.map(cat => {
        const items = progress[cat.key] || [];
        if (items.length === 0) return null;

        return (
          <div key={cat.key} className={`mb-4 last:mb-0 pl-3 border-l-[3px] ${cat.colorBorder}`}>
            <h4 className={`text-sm font-medium ${cat.colorLabel} mb-2 flex items-center gap-1.5`}>
              <cat.Icon size={16} weight="bold" />
              <span>{cat.label}</span>
              <span className="text-gray-400 font-normal">({items.length})</span>
            </h4>
            <div className="flex flex-wrap gap-2">
              {items.map((item, idx) => (
                <div
                  key={idx}
                  className={`relative px-3 py-1.5 pb-2.5 ${cat.colorBg} ${cat.colorText} rounded-full text-sm font-medium shadow-sm hover:shadow-md transition-shadow`}
                >
                  <span>{item.topic}</span>
                  {item.mastery !== undefined && (
                    <div className="absolute bottom-0.5 left-1/2 transform -translate-x-1/2 w-3/4 h-1 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${cat.colorBar}`}
                        style={{ width: `${item.mastery}%` }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProgressTracker;
